import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './ContactSalesModal.module.css';
import ReCAPTCHA from "react-google-recaptcha";
import { MdCheckCircle, MdEdit } from "react-icons/md";
import { WEBApi } from '../../API/WEBApi';

interface Props {
    show: boolean;
    onClose: () => void;
}

const ContactSalesModal: React.FC<Props> = ({ show, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const firstNameRef = useRef<HTMLInputElement>(null);

    const [countdown, setCountdown] = useState<number | null>(null);
    const countdownRef = useRef<NodeJS.Timeout | null>(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        propertyType: '',
        message: '',
    });

    // Reset local state when modal closes
    useEffect(() => {
        if (!show) {
            resetForm();
        } else {
            setTimeout(() => firstNameRef.current?.focus(), 300);
        }
    }, [show]);

    useEffect(() => {
        if (countdown === null) return;
        if (countdown === 0) {
            onClose();
            return;
        }
        countdownRef.current = setTimeout(() => {
            setCountdown(prev => (prev !== null ? prev - 1 : null));
        }, 1000);

        return () => {
            if (countdownRef.current) clearTimeout(countdownRef.current);
        };
    }, [countdown, onClose]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear specific error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isValidUSPhone = (phone: string) => /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(phone);

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!isValidEmail(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.phone) {
            newErrors.phone = 'Phone number is required';
        } else if (!isValidUSPhone(formData.phone)) {
            newErrors.phone = 'Use format: 123-456-7890';
        }
        if (!formData.propertyType) newErrors.propertyType = 'Please select a property type';
        if (!formData.message) newErrors.message = "Message is required";


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);

        if (!validateForm()) return;

        if (!showCaptcha) {
            setShowCaptcha(true);
            return;
        }

        if (!recaptchaToken) {
            setErrorMessage('Please complete the CAPTCHA.');
            return;
        }

        try {
            setIsSubmitting(true);
            await WEBApi.createNewContact({ ...formData, recaptchaToken });
            setFormSubmitted(true);
            setCountdown(5);
        } catch (error) {
            setErrorMessage('Submission failed. Please try again.');
            recaptchaRef.current?.reset();
            setRecaptchaToken(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        if (countdownRef.current) clearTimeout(countdownRef.current);
        setCountdown(null);
        setFormSubmitted(false);
        setShowCaptcha(false);
        setRecaptchaToken(null);
        setErrorMessage(null);
        setErrors({});
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            propertyType: '',
            message: '',
        });
    };

    return (
        <Modal show={show} onHide={onClose} centered contentClassName={styles['modal-content']} dialogClassName={styles['modal-main']}>
            <Modal.Header closeButton className={styles['modal-header']}>
                <div className={styles['header-content']}>
                    <Modal.Title className={styles['modal-title']}>Get In Touch</Modal.Title>
                    <p className={styles['modal-subtitle']}>Fill out the form below and our team will reach out shortly.</p>
                </div>
            </Modal.Header>

            <Modal.Body className={styles['modal-body']}>
                {formSubmitted ? (
                    <div className={styles['success-container']}>
                        <div className={styles['icon-circle']}>
                            <MdCheckCircle size={60} color="#22c55e" />
                        </div>
                        <h4>Request Sent!</h4>
                        <p>Thank you for reaching out to <strong>BreatheEV</strong>. We have received your inquiry.</p>
                        {countdown !== null && (
                            <p className={styles['auto-close-text']}>
                                Closing in <strong>{countdown}s</strong>
                            </p>
                        )}
                        <button onClick={resetForm} className={styles['reset-button']}>Send another message</button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className={styles['contact-form']} noValidate>
                        <div className={styles['input-grid']}>
                            <div className={styles['form-group']}>
                                <label>First Name</label>
                                <input
                                    ref={firstNameRef}
                                    name="firstName"
                                    placeholder="John"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    readOnly={showCaptcha}
                                    className={errors.firstName ? styles['input-error'] : ''}
                                />
                                {errors.firstName && <span className={styles['field-error']}>{errors.firstName}</span>}
                            </div>
                            <div className={styles['form-group']}>
                                <label>Last Name</label>
                                <input
                                    name="lastName"
                                    placeholder="Doe"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    readOnly={showCaptcha}
                                    className={errors.lastName ? styles['input-error'] : ''}
                                />
                                {errors.lastName && <span className={styles['field-error']}>{errors.lastName}</span>}
                            </div>
                        </div>

                        <div className={styles['form-group']}>
                            <label>Email Address</label>
                            <input
                                name="email"
                                type="email"
                                placeholder="john@example.com"
                                value={formData.email}
                                onChange={handleChange}
                                readOnly={showCaptcha}
                                className={errors.email ? styles['input-error'] : ''}
                            />
                            {errors.email && <span className={styles['field-error']}>{errors.email}</span>}
                        </div>

                        <div className={styles['form-group']}>
                            <label>Phone Number</label>
                            <input
                                name="phone"
                                placeholder="(555) 000-0000"
                                value={formData.phone}
                                onChange={handleChange}
                                readOnly={showCaptcha}
                                className={errors.phone ? styles['input-error'] : ''}
                            />
                            {errors.phone && <span className={styles['field-error']}>{errors.phone}</span>}
                        </div>

                        <div className={styles['form-group']}>
                            <label>Property Type</label>
                            <select
                                name="propertyType"
                                value={formData.propertyType}
                                onChange={handleChange}
                                disabled={showCaptcha}
                                className={errors.propertyType ? styles['input-error'] : ''}
                            >
                                <option value="" disabled>Select property type...</option>
                                <option value="Multi-Family">Multi-Family</option>
                                <option value="Commercial">Commercial</option>
                                <option value="Retail">Retail</option>
                                <option value="Hospitality">Hospitality</option>
                                <option value="University">University</option>
                            </select>
                            {errors.propertyType && <span className={styles['field-error']}>{errors.propertyType}</span>}
                        </div>

                        <div className={styles['form-group']}>
                            <label>Message</label>
                            <textarea
                                name="message"
                                rows={3}
                                placeholder="How can we help you?"
                                value={formData.message}
                                onChange={handleChange}
                                readOnly={showCaptcha}
                                className={errors.message ? styles['input-error'] : ''}
                            />
                            {errors.message && <span className={styles['field-error']}>{errors.message}</span>}

                        </div>

                        {showCaptcha && (
                            <div className={styles['captcha-section']}>
                                <div className={styles['captcha-header']}>
                                    <span>Complete CAPTCHA</span>
                                    <button type="button" onClick={() => setShowCaptcha(false)} className={styles['edit-btn']}>
                                        <MdEdit /> Edit Form
                                    </button>
                                </div>
                                <ReCAPTCHA
                                    sitekey="6Lfr5HMrAAAAAL7YP3cT6418M2iezHk7QGZkdIEp"
                                    ref={recaptchaRef}
                                    onChange={setRecaptchaToken}
                                />
                            </div>
                        )}

                        {errorMessage && <div className={styles['error-banner']}>{errorMessage}</div>}

                        <button
                            type="submit"
                            className={`${styles['submit-btn']} ${showCaptcha ? styles['btn-final'] : ''}`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span>Submitting...</>
                            ) : showCaptcha ? (
                                'Confirm & Send'
                            ) : (
                                'Continue'
                            )}
                        </button>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ContactSalesModal;